<script setup lang="ts">
import type { Node, Edge } from "@vue-flow/core"
import { VueFlow, useVueFlow } from "@vue-flow/core"

// these components are only shown as examples of how to use a custom node or edge
// you can find many examples of how to create these custom components in the examples page of the docs
import VueFlowStepNode from "./VueFlowStepNode.vue"
import VueFlowSpecialEdge from "./VueFlowSpecialEdge.vue"
import VueFlowStepHasIntermediateNode from "./VueFlowStepHasIntermediateNode.vue"
import VueFlowStepFinalNode from "./VueFlowStepFinalNode.vue"
import VueFlowTemplateGroupNode from "./VueFlowTemplateGroupNode.vue"

const props = defineProps<{
	id: string
	nodes: Node[]
	edges: Edge[]
	interactive?: boolean
	disabled?: boolean
	fitViewOnInit?: boolean
	defaultViewport?: { x: number; y: number; zoom: number }
}>()

const emit = defineEmits<{
	(e: "nodeClick", node: Node): void
	(e: "nodeDragStart", event: { node: Node }): void
	(e: "nodeDragStop", event: { node: Node; nodes: Node[] }): void
	(e: "viewportChange", viewport: { x: number; y: number; zoom: number }): void
	(e: "paneReady"): void
}>()

const { getNodes, fitView, setViewport, getViewport, project } = useVueFlow(props.id)

defineExpose({ fitView, getNodes, setViewport, getViewport, project })

function handleNodeClick(node: Node) {
	// Block click events when disabled (e.g., during loading)
	if (props.disabled) return
	emit("nodeClick", node)
}

function handleNodeDragStart(dragEvent: { node: any }) {
	emit("nodeDragStart", { node: dragEvent.node })
}

function handleNodeDragStop(dragEvent: { node: any; nodes: any[] }) {
	// Emit ALL nodes (not just dragged ones) so positions for every node are captured
	emit("nodeDragStop", { node: dragEvent.node, nodes: getNodes.value })
}
</script>

<template>
	<div :id="id + '-container'" class="vue-flow-container" style="width: 100%; height: 100%; position: relative">
		<!-- Overlay to block all interactions when disabled -->
		<div v-if="disabled" class="vue-flow-overlay"></div>
		<!-- :pan-on-drag="false" :zoom-on-scroll="false" :zoom-on-pinch="false" :zoom-on-double-click="false"
			:nodes-draggable="false" :elements-selectable="false" :prevent-scrolling="true" -->
		<VueFlow
			:id="id"
			:default-viewport="defaultViewport ?? { x: 20, y: 70 }"
			:fit-view-on-init="fitViewOnInit !== false"
			:nodes="nodes"
			:edges="edges"
			:elements-selectable="interactive && !disabled"
			:prevent-scrolling="false"
			:nodes-draggable="true"
			@node-click="(nodeEvent) => handleNodeClick(nodeEvent.node)"
			@node-drag-start="handleNodeDragStart"
			@node-drag-stop="handleNodeDragStop"
			@move-end="() => emit('viewportChange', getViewport())"
			@pane-ready="() => emit('paneReady')"
			style="width: 100%; height: 100%"
		>
			<!-- bind your custom node types to a component by using slots, slot names are always `node-<type>` -->
			<template #node-step="stepNodeProps">
				<VueFlowStepNode v-bind="stepNodeProps" />
			</template>
			<template #node-has-intermediate="stepNodeProps">
				<VueFlowStepHasIntermediateNode v-bind="stepNodeProps" />
			</template>
			<template #node-final="stepNodeProps">
				<VueFlowStepFinalNode v-bind="stepNodeProps" />
			</template>
			<template #node-template-group="groupNodeProps">
				<VueFlowTemplateGroupNode v-bind="groupNodeProps" />
			</template>
			<!-- bind your custom edge type to a component by using slots, slot names are always `edge-<type>` -->
			<template #edge-special="specialEdgeProps">
				<VueFlowSpecialEdge v-bind="specialEdgeProps" />
			</template>
			<!-- pass-through slot so parents can inject SVG overlays into the zoom-pane coordinate space -->
			<template #zoom-pane>
				<slot name="overlay" />
			</template>
		</VueFlow>
	</div>
</template>

<style>
/* import the necessary styles for Vue Flow to work */
@import "@vue-flow/core/dist/style.css";

/* import the default theme, this is optional but generally recommended */
@import "@vue-flow/core/dist/theme-default.css";

/* Overlay that blocks all interactions when disabled */
.vue-flow-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 1000;
	background: transparent;
	cursor: default;
}

/* Styling for the group/box around selected steps */
.vue-flow__node-group {
	background-color: color-mix(in srgb, var(--color-primary, #3b82f6) 8%, transparent) !important;
	border: 3px solid var(--color-primary, #3b82f6) !important;
	border-radius: 8px !important;
	z-index: -1 !important;
}

/* Remove default handles from group and template-group nodes */
.vue-flow__node-group .vue-flow__handle,
.vue-flow__node-template-group .vue-flow__handle,
[data-id="selected-steps-group"] .vue-flow__handle {
	display: none !important;
	width: 0 !important;
	height: 0 !important;
}

/* Additional styling for nodes inside the box */
.vue-flow__node.step-in-box {
	z-index: 1 !important;
}
</style>
